/**
 * Release row component for admin releases table.
 */

import * as stylex from "@stylexjs/stylex";

import { badge, button, table, text } from "@animus/primaris";

import type { Release, ReleasePlatform } from "./types.ts";
import { styles } from "./styles.stylex.ts";

interface ReleaseRowProps {
  release: Release;
  isPinned: boolean;
  onPin: (release: Release) => void;
  onUnpin: (platform: ReleasePlatform) => void;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortenSha(sha: string): string {
  return sha.slice(0, 7);
}

function getPlatformBadge(platform: ReleasePlatform) {
  switch (platform) {
    case "web":
      return badge.blue;
    case "marketing":
      return badge.purple;
    case "mobile":
      return badge.green;
    default:
      return badge.neutral;
  }
}

function ReleaseRow({ release, isPinned, onPin, onUnpin }: ReleaseRowProps) {
  const isCompatibilityWarning =
    release.platform === "mobile" && !release.isCompatible;

  return (
    <tr {...stylex.props(table.tr)}>
      <td {...stylex.props(table.td)}>
        <div {...stylex.props(styles.badges)}>
          <span
            {...stylex.props(badge.base, getPlatformBadge(release.platform))}
          >
            {release.platform}
          </span>
          {release.isActive && (
            <span {...stylex.props(badge.base, badge.green)}>Active</span>
          )}
          {isPinned && (
            <span {...stylex.props(badge.base, badge.yellow)}>Pinned</span>
          )}
          {isCompatibilityWarning && (
            <span
              {...stylex.props(badge.base, badge.orange)}
              title={release.compatibilityWarning ?? "May be incompatible"}
            >
              Compat Warning
            </span>
          )}
        </div>
      </td>
      <td {...stylex.props(table.td)}>
        {release.githubUrl ? (
          <a
            {...stylex.props(styles.link)}
            href={release.githubUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {shortenSha(release.gitSha)}
          </a>
        ) : (
          <span {...stylex.props(styles.mono, text.muted)}>
            {shortenSha(release.gitSha)}
          </span>
        )}
      </td>
      <td {...stylex.props(table.td, styles.maxWidth, text.sm, text.muted)}>
        {release.commitMessage ?? "-"}
      </td>
      <td {...stylex.props(table.td, text.sm, text.muted)}>
        {release.author ?? "-"}
      </td>
      <td {...stylex.props(table.td, text.sm, text.muted)}>
        {formatDate(release.timestamp)}
      </td>
      <td {...stylex.props(table.td, table.tdRight)}>
        {isPinned ? (
          <button
            {...stylex.props(button.base, button.warning, button.small)}
            onClick={() => onUnpin(release.platform)}
            type="button"
          >
            Unpin
          </button>
        ) : (
          <button
            {...stylex.props(button.base, button.secondary, button.small)}
            onClick={() => onPin(release)}
            type="button"
          >
            Pin
          </button>
        )}
      </td>
    </tr>
  );
}

export { ReleaseRow, type ReleaseRowProps };
